import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import {
    CreditorPayment,
    PractitionerVisitsSummary,
    SettlementDebtSummary
} from '../../../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';
import { StatusTone } from '../../../theme/guideTokens';
import { statusMeta } from '../../Settlements/SettlementList/SettlementList.Logic';
import { isOtherReceiver } from '../../../functions/PractitionerAccountStatement.Functions';

/**
 * Cuentas del estado de cuenta del profesional, sin React ni store. Regla de
 * oro: nunca se suman importes de monedas distintas.
 */

export { isOtherReceiver };

const round = (value: number): number => Math.round(value * 100) / 100;
const upper = (code: string | null | undefined): string => (code || 'ARS').toUpperCase();

//#region Período

export type PeriodKey = 'month' | 'quarter' | 'year' | 'all';

export const PERIOD_KEYS: PeriodKey[] = ['month', 'quarter', 'year', 'all'];

export interface Period {
    from?: Date;
    to?: Date;
}

/** El rango de cada atajo, contado desde hoy. "Todo" no tiene límites. */
export const periodRange = (key: PeriodKey, today: Date = new Date()): Period => {
    const year = today.getFullYear();
    const month = today.getMonth();
    const end = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
    switch (key) {
        case 'month':
            return { from: new Date(year, month, 1), to: end(new Date(year, month + 1, 0)) };
        case 'quarter': {
            const start = month - (month % 3);
            return { from: new Date(year, start, 1), to: end(new Date(year, start + 3, 0)) };
        }
        case 'year':
            return { from: new Date(year, 0, 1), to: end(new Date(year, 11, 31)) };
        default:
            return {};
    }
};

export const inPeriod = (value: Date | string | null | undefined, period: Period): boolean => {
    if (!period.from && !period.to) return true;
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    return (!period.from || date >= period.from) && (!period.to || date <= period.to);
};

//#endregion Período

//#region Monedas

/** Monedas que aparecen en el estado de cuenta, ordenadas. */
export const currenciesOf = (summaries: SettlementDebtSummary[], payments: CreditorPayment[], debts: Debt[] = []): string[] => {
    const set = new Set<string>();
    summaries.forEach((item) => set.add(upper(item.currencyCode)));
    payments.forEach((item) => set.add(upper(item.currencyCode)));
    debts.forEach((item) => set.add(upper(item.currencyCode)));
    return Array.from(set).sort();
};

const matchesCurrency = (code: string | null | undefined, currency?: string): boolean => !currency || upper(code) === currency;

//#endregion Monedas

//#region Saldo

export interface BalanceByCurrency {
    currency: string;
    /** Lo que le debemos: pendiente total, sin importar el período. */
    pending: number;
    /** Lo liquidado en total (histórico). */
    original: number;
    /** Lo ya pagado de lo liquidado (histórico). */
    paid: number;
    pendingDebts: number;
    /** Liquidaciones con saldo pendiente. */
    pendingSettlements: number;
    /** Pagado en el período, imputado a deudas del profesional, en moneda del pago. */
    paidInPeriod: number;
    paymentsInPeriod: number;
    /** De lo pagado en el período, lo que se registró a nombre de otro receptor. */
    paidToOthersInPeriod: number;
}

/**
 * El encabezado del estado de cuenta, una fila por moneda. Lo pendiente y lo
 * liquidado salen del resumen por liquidación (endpoint o armado con las
 * deudas); lo pagado en el período sale de los pagos.
 */
export const buildBalance = (input: {
    summaries: SettlementDebtSummary[];
    payments: CreditorPayment[];
    debts: Debt[];
    period: Period;
    practitionerId: string;
    currency?: string;
}): BalanceByCurrency[] => {
    const rows = new Map<string, BalanceByCurrency>();
    const rowOf = (currency: string): BalanceByCurrency => {
        const existing = rows.get(currency);
        if (existing) return existing;
        const created: BalanceByCurrency = {
            currency, pending: 0, original: 0, paid: 0, pendingDebts: 0, pendingSettlements: 0,
            paidInPeriod: 0, paymentsInPeriod: 0, paidToOthersInPeriod: 0
        };
        rows.set(currency, created);
        return created;
    };

    const pendingSettlementIds = new Map<string, Set<number>>();
    input.summaries.filter((item) => matchesCurrency(item.currencyCode, input.currency)).forEach((item) => {
        const row = rowOf(upper(item.currencyCode));
        row.pending = round(row.pending + (item.pendingAmount ?? 0));
        row.original = round(row.original + (item.originalAmount ?? 0));
        row.paid = round(row.paid + (item.paidAmount ?? 0));
        if ((item.pendingAmount ?? 0) > 0 && item.settlementId !== null && item.settlementId !== undefined) {
            const ids = pendingSettlementIds.get(row.currency) ?? new Set<number>();
            ids.add(item.settlementId);
            pendingSettlementIds.set(row.currency, ids);
        }
    });
    pendingSettlementIds.forEach((ids, currency) => { rowOf(currency).pendingSettlements = ids.size; });

    input.debts
        .filter((debt) => matchesCurrency(debt.currencyCode, input.currency) && !debt.isDebtSettled && (debt.pendingAmount ?? 0) > 0)
        .forEach((debt) => { rowOf(upper(debt.currencyCode)).pendingDebts += 1; });

    input.payments
        .filter((payment) => matchesCurrency(payment.currencyCode, input.currency) && inPeriod(payment.paymentDate, input.period))
        .forEach((payment) => {
            const row = rowOf(upper(payment.currencyCode));
            const amount = payment.totalImputedToCreditor ?? payment.amount ?? 0;
            row.paidInPeriod = round(row.paidInPeriod + amount);
            row.paymentsInPeriod += 1;
            if (isOtherReceiver(payment, input.practitionerId)) row.paidToOthersInPeriod = round(row.paidToOthersInPeriod + amount);
        });

    return Array.from(rows.values()).sort((a, b) => a.currency.localeCompare(b.currency));
};

//#endregion Saldo

//#region Por liquidación

export type SettlementPaymentState = 'paid' | 'partial' | 'unpaid';

export interface SettlementRow {
    key: string;
    settlementId: number | null;
    name: string;
    periodFrom?: Date | string;
    periodTo?: Date | string;
    status?: number;
    currency: string;
    original: number;
    paid: number;
    pending: number;
    /** 0–100: cuánto de lo liquidado ya se pagó. */
    progress: number;
    debtCount: number;
    paymentState: SettlementPaymentState;
}

export const settlementLabel = (settlementId: number | null | undefined, settlements: Settlement[]): string => {
    if (settlementId === null || settlementId === undefined) return 'Sin liquidación';
    const settlement = settlements.find((item) => item.id === settlementId);
    return settlement?.name?.trim() || `LIQ-${settlementId}`;
};

export const paymentStateOf = (original: number, pending: number): SettlementPaymentState => {
    if (pending <= 0) return 'paid';
    if (pending < original) return 'partial';
    return 'unpaid';
};

export const progressOf = (original: number, paid: number): number => {
    if (!(original > 0)) return paid > 0 ? 100 : 0;
    return Math.max(0, Math.min(100, Math.round((paid / original) * 100)));
};

/**
 * Resumen de deudas por (liquidación, moneda) unido con la liquidación (nombre,
 * período y estado). Ordenado por moneda y, dentro, lo más nuevo arriba.
 */
export const buildSettlementRows = (
    summaries: SettlementDebtSummary[],
    settlements: Settlement[],
    currency?: string
): SettlementRow[] =>
    summaries
        .filter((item) => matchesCurrency(item.currencyCode, currency))
        .map((item) => {
            const settlement = item.settlementId !== null ? settlements.find((candidate) => candidate.id === item.settlementId) : undefined;
            const original = round(item.originalAmount ?? 0);
            const paid = round(item.paidAmount ?? 0);
            const pending = round(item.pendingAmount ?? 0);
            return {
                key: `${item.settlementId ?? 'none'}|${upper(item.currencyCode)}`,
                settlementId: item.settlementId ?? null,
                name: settlementLabel(item.settlementId, settlements),
                periodFrom: settlement?.settlementDateRangeInit ?? item.firstDebtDate ?? undefined,
                periodTo: settlement?.settlementDateRangeEnd ?? item.lastDebtDate ?? undefined,
                status: settlement?.status,
                currency: upper(item.currencyCode),
                original,
                paid,
                pending,
                progress: progressOf(original, paid),
                debtCount: item.debtCount ?? 0,
                paymentState: paymentStateOf(original, pending)
            };
        })
        .sort((a, b) =>
            a.currency.localeCompare(b.currency) ||
            new Date(b.periodFrom ?? 0).getTime() - new Date(a.periodFrom ?? 0).getTime() ||
            (b.settlementId ?? -1) - (a.settlementId ?? -1)
        );

export interface CurrencySubtotal {
    currency: string;
    original: number;
    paid: number;
    pending: number;
    count: number;
}

/** Acumulado de la tabla por moneda (subtotal). */
export const subtotalsByCurrency = (rows: SettlementRow[]): CurrencySubtotal[] => {
    const totals = new Map<string, CurrencySubtotal>();
    rows.forEach((row) => {
        const total = totals.get(row.currency) ?? { currency: row.currency, original: 0, paid: 0, pending: 0, count: 0 };
        total.original = round(total.original + row.original);
        total.paid = round(total.paid + row.paid);
        total.pending = round(total.pending + row.pending);
        total.count += 1;
        totals.set(row.currency, total);
    });
    return Array.from(totals.values()).sort((a, b) => a.currency.localeCompare(b.currency));
};

/** Una fila se puede pagar si es de una liquidación y le queda saldo. */
export const canPayRow = (row: SettlementRow): boolean => row.settlementId !== null && row.pending > 0;

/** Tono de la pastilla de estado de la liquidación, en los tonos de la guía. */
export const settlementStatusTone = (status: number | undefined): StatusTone => {
    switch (statusMeta(status).tone) {
        case 'paid': return 'success';
        case 'confirmed': return 'confirmed';
        case 'finished': return 'brand';
        case 'progress': return 'info';
        case 'critical': return 'warning';
        case 'danger': return 'danger';
        default: return 'neutral';
    }
};

export const settlementStatusKey = (status: number | undefined): string => statusMeta(status).key;

//#endregion Por liquidación

//#region Pagos

/** Pagos del período y la moneda elegidos. */
export const filterPayments = (payments: CreditorPayment[], period: Period, currency?: string): CreditorPayment[] =>
    payments.filter((payment) => matchesCurrency(payment.currencyCode, currency) && inPeriod(payment.paymentDate, period));

/** Liquidaciones a las que se imputó el pago, sin repetir. */
export const paymentSettlementIds = (payment: CreditorPayment): (number | null)[] =>
    Array.from(new Set((payment.imputations ?? []).map((item) => item.settlementId ?? null)));

//#endregion Pagos

//#region Deudas pendientes

export const pendingDebtsOf = (debts: Debt[], currency?: string): Debt[] =>
    debts
        .filter((debt) => !debt.isDebtSettled && (debt.pendingAmount ?? 0) > 0 && matchesCurrency(debt.currencyCode, currency))
        .sort((a, b) => new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime());

//#endregion Deudas pendientes

//#region Actividad

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export interface MonthCount {
    key: string;
    label: string;
    count: number;
}

/** Visitas por mes, en orden cronológico, listas para el gráfico de barras. */
export const visitsByMonth = (visits: PractitionerVisitsSummary | undefined): MonthCount[] =>
    [...(visits?.byMonth ?? [])]
        .sort((a, b) => a.year - b.year || a.month - b.month)
        .map((item) => ({
            key: `${item.year}-${String(item.month).padStart(2, '0')}`,
            label: `${MONTHS[(item.month - 1 + 12) % 12]} ${String(item.year).slice(-2)}`,
            count: item.count
        }));

/** Estados de paciente de la historia clínica (EmrProtocolPatientStatusEnum): 0 Screening … 9 PreScreening. */
export const PATIENT_STATUS_COUNT = 10;

/**
 * Clave i18n del estado de un paciente (`patientStatus.s{n}`). El API manda el
 * nombre en inglés: se traduce por número y, si el número no se conoce, se usa
 * el nombre tal cual (o "Sin estado").
 */
export const patientStatusKey = (status: number | null | undefined): string | undefined =>
    status !== null && status !== undefined && Number.isInteger(status) && status >= 0 && status < PATIENT_STATUS_COUNT
        ? `patientStatus.s${status}`
        : undefined;

/** "PRESTO-01 · Estudio Presto": número y nombre del protocolo, sin repetir. */
export const protocolLabel = (protocolNumber: string | null | undefined, protocolName: string | null | undefined): string => {
    const number = (protocolNumber || '').trim();
    const name = (protocolName || '').trim();
    if (number && name && number !== name) return `${number} · ${name}`;
    return number || name || '—';
};

//#endregion Actividad
